/**
 * First version of quick entry. The default export {@link QuickEntry} is not rendered anywhere:
 * every screen renders `QuickEntryV2.tsx`, which has the same state, calls and behaviour (see its
 * header) and renders the V2 views. Changes to quick entry go there.
 *
 * Legacy: don't copy. Dead and to be removed: the callers move to `QuickEntryV2.tsx`'s
 * `QuickEntryDataType` / `SetQuickEntryData`, then this file, its two views
 * (`Helpers/QuickEntryDesktop.tsx`, `Helpers/QuickEntryMobile.tsx`) and the enum copy in
 * `Helpers/QuickEntry.models.tsx` are deleted.
 *
 * What is still live in this file:
 * - `QuickEntryDataType`, imported by `ShoppingCart/ShoppingCartDesktop.tsx`,
 *   `ShoppingCartMobile.tsx`, `ShoppingListDetails/Helpers/ShoppingListDetailsDesktopContent.tsx`,
 *   `ShoppingLIstMobileContent.tsx` and `QuoteDetails/QuoteDetails.tsx`, which pass it to
 *   `QuickEntryV2`.
 * - `SetQuickEntryData`, imported only by `ShoppingListDetailsDesktopContent.tsx`,
 *   `ShoppingLIstMobileContent.tsx` and `QuoteDetails/QuoteDetails.tsx`, which cast their state
 *   setters to it for `QuickEntryV2`'s `setData`.
 *
 * Keep `QuickEntryDataType` identical to `QuickEntryV2.tsx` →
 * `QuickEntryDataType` in name and members; TypeScript accepts one for the other only while they
 * match.
 *
 * Differences from V2: renders `Helpers/QuickEntryDesktop.tsx` / `Helpers/QuickEntryMobile.tsx`,
 * which use `QuantityUOM` from `@ryerson/frontend.quantity-uom-inputs` instead of
 * `Helpers/V2/QuantityUOMV2.tsx`, and does not pass `isQuickEntryValid` to the views.
 */
import { useApplication } from "@ryerson/frontend.application";
import React, { useEffect, useRef, useState } from "react";
import { APIUrl } from "@enums/apiUrl.enums";
import {
	useDebounce,
	useOnClickOutside,
	useCookie,
	getCustomerPartNumber,
	Subscribable,
	getUsersDefaultUomValue,
	ShoppingCartDataType,
	DraftQuoteLineItem,
	EventParamsItem,
	EventParams,
	sendGAEvent,
	GoogleAnalyticsEventName,
} from "@ryerson/frontend.common";
import { Product } from "@ryerson/frontend.common";
import { Media } from "@ryerson/frontend.next";
import { getZipCode } from "@components/AccountSettings/Helpers/zipCode";

import QuickEntryDesktop from "./Helpers/QuickEntryDesktop";
import QuickEntryMobile from "./Helpers/QuickEntryMobile";
import { SEARCH_MIN_LENGTH } from "@components/Search/Helpers/Search.models";
import { useEcommerce } from "@ryerson/frontend.e-commerce";

import {
	addProductsToShoppingList,
	ShoppingListProduct,
} from "@components/Helper/shoppingListHelper";
import { ShoppingListItem } from "@ryerson/frontend.common";
import { getItem } from "@components/Hooks";
import { lb, lbs, pcs } from "@ryerson/common.uom";
import { addLineItemAPI } from "@components/QuoteDetails/Helpers/HelperFunctions";
import { defaultPricingData } from "@components/ShoppingCart/ShoppingCart.model";

import { QuickEntryProps } from "./Helpers/QuickEntry.models";
import { Form } from "@ryerson/common.process-cut";
import { isAxiosError } from "@components/Helper/TypeHelpers";
import { AxiosResponse } from "axios";

/**
 * Which collection quick entry adds to. This is the copy every caller imports and passes to
 * `QuickEntryV2`; it must match `QuickEntryV2.tsx` → `QuickEntryDataType` in name and members.
 */
export enum QuickEntryDataType {
	ShoppingCart = 1,
	ShoppingList = 2,
	QuoteList = 3,
}

/** One row of whichever collection quick entry adds to: a cart line, list item or quote line. */
type QuickEntryData = ShoppingCartDataType | ShoppingListItem | DraftQuoteLineItem;
/**
 * State setter for the caller's rows. Imported by the list-details and quote-details callers to
 * cast their own setters for `QuickEntryV2`'s `setData` prop.
 */
export type SetQuickEntryData = React.Dispatch<React.SetStateAction<QuickEntryData[]>>;
/* 	| React.Dispatch<React.SetStateAction<ShoppingListItem[]>>
	| React.Dispatch<React.SetStateAction<DraftQuoteLineItem[]>>; */

/**
 * Props of the unrendered {@link QuickEntry}; same fields as `QuickEntryV2.tsx` →
 * `QuickEntryTypeV2`.
 */
export type QuickEntryType = {
	dataType: QuickEntryDataType;
	data: QuickEntryData[];
	setData: SetQuickEntryData;
	showQuickEntry: boolean;
	setShowQuickEntry: React.Dispatch<React.SetStateAction<boolean>>;
	setPricingOfItems?: (value: any) => void;
	id?: any;
	itemNumber?: number | undefined;
	updateShoppingCart?: () => void;
};

/** A UOM dropdown option. Not imported by any other file. */
export interface DropdownOption {
	display: string;
	value: string;
}

/**
 * Quick entry, first version. Not rendered by any screen; `QuickEntryV2.tsx` → `QuickEntryV2` is
 * the rendered component and documents the behaviour both share.
 */
const QuickEntry: React.FC<QuickEntryType> = ({
	dataType,
	data,
	setData,
	showQuickEntry,
	setShowQuickEntry,
	setPricingOfItems,
	id,
	itemNumber,
	updateShoppingCart,
}) => {
	let debouncedSearchInput: string;

	let NOTIFY_TIMEOUT = 5000;

	// Use default option until API get ready
	const [searchTerm, setSearchTerm] = useState<string | number>("");
	const [displaySearchTerm, setDisplaySearchTerm] = useState<string | number>("");
	const [showDropdown, setShowDropdown] = useState<boolean>(false);
	const [isError, setIsError] = useState<boolean>(false);
	const [searchResult, setSearchResult] = useState<Array<Product>>([]);
	const [selectedProduct, setSelectedProduct] = useState<Product | undefined>();
	const [quantity, setQuantity] = useState<number | undefined>();
	const [qtyUOM, setQtyUOM] = useState<string>(lbs.value);
	const [qtyUOMDropdown, setQtyUOMDropdown] = useState<DropdownOption[]>([lbs]);
	const [priceUOM, setPriceUOM] = useState<string>(lb.value);
	const [priceUOMDropdown, setPriceUOMDropdown] = useState<DropdownOption[]>([lb]);
	const [itemAddSuccess, setItemAddSuccess] = useState(false);
	const [itemAddFail, setItemAddFail] = useState(false);
	const [, setShoppingListData] = useCookie("addToShoppingList", "");

	const searchInputContainerRef = useRef<HTMLElement>(null);
	const searchResultsRef = useRef<HTMLElement>(null);
	const [hasMounted, setHasMounted] = useState<boolean>(false);

	// added to support new quantityUOM Component
	const [isQuickEntrySubmitted, setIsQuickEntrySubmitted] = React.useState<boolean>(false);
	const [isQuickEntrValid, setIsQuickEntrValid] = React.useState<boolean>(false);

	// PUB-SUB: Instantiated Subscribable , in this case calling it stateMessenger
	const [stateMessenger] = React.useState<Subscribable<string>>(new Subscribable<string>());

	useOnClickOutside([searchInputContainerRef, searchResultsRef], () => {
		setShowDropdown(false);
	});

	const { setCartPricingData, addItemToCart, selectedShipToAddress, customerPickup, salesPlant } =
		useEcommerce();

	const {
		axiosInstance,
		zipCode,
		user: { isLoggedIn },
		profile,
	} = useApplication();

	useEffect(() => {
		if (itemAddSuccess) {
			setTimeout(() => {
				setItemAddSuccess(false);
			}, NOTIFY_TIMEOUT);
		}
		if (itemAddFail) {
			setTimeout(() => {
				setItemAddFail(false);
			}, NOTIFY_TIMEOUT);
		}
	}, [itemAddSuccess, itemAddFail]);

	const DEBOUNCE_TIME = 1000;

	debouncedSearchInput = searchTerm.toString();
	const debouncedSearchTerm = useDebounce(debouncedSearchInput, DEBOUNCE_TIME);

	const resetToDefault = () => {
		setSelectedProduct(undefined);
		setShowDropdown(false);
		setSearchResult([]);
		setIsError(false);
		setQuantity(undefined);

		setIsQuickEntrySubmitted(false);

		setQtyUOM(lbs.value);
		setPriceUOM(lb.value);
		setQtyUOMDropdown([lbs]);
		setPriceUOMDropdown([lb]);
	};

	const isProductDetailsValid = (selectedProduct: Product): boolean => {
		if (
			(!selectedProduct.weightPerFoot &&
				!selectedProduct.weightPerSquareFoot &&
				!selectedProduct.weightPerPiece &&
				selectedProduct.form !== Form.COIL) ||
			selectedProduct.cutFlag === "A"
		) {
			return true;
		} else {
			return false;
		}
	};

	const updateQuantityUom = (value: string) => {
		setQtyUOM(value);
	};

	useEffect(() => {
		const searchTermLength = searchTerm?.toString().length;
		if (searchTermLength < 1 || debouncedSearchTerm !== searchTerm) {
			resetToDefault();
		} else if (searchTermLength >= SEARCH_MIN_LENGTH && debouncedSearchTerm === searchTerm) {
			let inputData = encodeURIComponent(searchTerm.toString().trim());
			let queryParams = `search-phrase=${inputData}`;

			queryParams += `&zip-code=${getZipCode(
				isLoggedIn,
				zipCode,
				selectedShipToAddress,
				customerPickup,
				salesPlant
			)}`;

			axiosInstance
				.get(`${APIUrl.SEARCH_PRODUCTS}?${queryParams}`)
				.then((response: any) => {
					let responseData = response?.data;
					if (responseData?.length > 0) {
						setSearchResult(responseData);
						setShowDropdown(true);
					} else {
						setIsError(true);
					}
				})
				.catch(() => {
					setIsError(true);
				});
		}
	}, [debouncedSearchTerm, searchTerm]);

	useEffect(() => {
		setHasMounted(true);
	}, []);

	const setUOMAndPriceUOMDropDown = (productItem: Product): void => {
		const { defaultQtyUom, defaultPricingUom, pricingUomOption, qtyUomOption } =
			getUsersDefaultUomValue(productItem, profile);

		setQtyUOM(defaultQtyUom);
		setPriceUOM(defaultPricingUom);
		setPriceUOMDropdown(pricingUomOption);
		setQtyUOMDropdown(qtyUomOption);
	};

	const onClickSelectItem = (productItem: Product): void => {
		setUOMAndPriceUOMDropDown(productItem);
		setSelectedProduct(productItem);
		setDisplaySearchTerm(productItem.fastPath);
		setShowDropdown(false);
		setQuantity(undefined);
		if (isProductDetailsValid(productItem)) {
			setQtyUOM(pcs.value);
			setQtyUOMDropdown([pcs]);
		}
		setIsError(false);
	};

	const onChangeText = (event: any): void => {
		const val = event.target.value;

		setItemAddSuccess(false);
		setItemAddFail(false);
		setSearchTerm(val);
		setDisplaySearchTerm(val);
	};

	// Validation Response called by QuantityUOM  Component
	const handleValidationResponse = (hasError: boolean, id: number | string) => {
		setIsQuickEntrValid(!hasError);
	};

	const onClickAddBelow = (callBack?: Function) => {
		setIsQuickEntrySubmitted(true);

		if (!isQuickEntrValid) {
			return false;
		}

		if (selectedProduct) {
			let lastIndex: number = Number(
				sessionStorage.getItem(
					dataType === QuickEntryDataType.ShoppingCart
						? "cartLastIndex"
						: dataType === QuickEntryDataType.ShoppingList
						? "shoppingListLastIndex"
						: "quoteListLastIndex"
				) || 0
			);
			let addToObject = {
				mm: selectedProduct?.materialMaster,
				qty: quantity,
				quom: qtyUOM,
				puom: priceUOM,
			};
			let newlyAddedDataObject: QuickEntryData = {
				id: lastIndex + 1,
				materialMaster: addToObject.mm,
				quantity: addToObject.qty || 0,
				quantityUOM: addToObject.quom,
				priceUOM: addToObject.puom,
				shoppingCartId: "",
				customerRefNumber: "",
				purchaseOrderNumber: "",
				notes: "",
				domesticRequired: false,
			};
			if (dataType === QuickEntryDataType.ShoppingCart && quantity) {
				addItemToCart(
					axiosInstance,
					isLoggedIn,
					[
						{
							ryersonSku: selectedProduct.materialMaster,
							customerPartNumber: getCustomerPartNumber(
								selectedProduct?.altPartNumber
							),
							quantity,
							quantityUom: qtyUOM,
							pricingUom: priceUOM,
							customerRefNumber: "",
							purchaseOrderNumber: "",
							notes: "",
							domesticRequired: false,
						},
					],
					isLoggedIn
						? (response) => {
								const responseData = response?.data;
								if (responseData) {
									if (updateShoppingCart) {
										updateShoppingCart();
									}
									let responsedCartData = responseData.map(
										(item: any): QuickEntryData => {
											return {
												id: item.id,
												shoppingCartId: item.shoppingCartId,
												materialMaster: item.ryersonSku,
												partNumber: item.customerPartNumber,
												quantity: item.quantity,
												quantityUOM: item.quantityUom,
												priceUOM: item.pricingUom,
												customerRefNumber: item.customerRefNumber,
												purchaseOrderNumber: item.purchaseOrderNumber,
												notes: item.notes,
												domesticRequired: item.domesticRequired,
											};
										}
									);
									let allCartItems = [...data, ...responsedCartData];

									callBack && callBack();
									handleOnSuccessAddItem(allCartItems);
								}
								/* eslint-disable-next-line */
						  }
						: () => {
								sessionStorage.setItem("cartLastIndex", (lastIndex + 1).toString());
								let allCartItems: QuickEntryData[] = [
									...data,
									newlyAddedDataObject,
								];
								if (updateShoppingCart) {
									updateShoppingCart();
								}

								callBack && callBack();
								handleOnSuccessAddItem(allCartItems);

								/* eslint-disable-next-line */
						  }
				);

				// Send a GA event with data about the item added to the cart
				const { materialMaster, fastPath, category, form } = selectedProduct;
				const addToCartGAEventParams = {
					items: [
						{
							item_id: materialMaster, // SKU of the item
							item_name: fastPath ?? "",
							index: 0, // it is the only item being added to the cart
							item_category: category as string, // typecasting as category should always be defined
							item_category2: form as string, // typecasting as form should always be defined
							item_variant: qtyUOM,
							quantity: Number(quantity),
						} as EventParamsItem, // casting as location_id is required in type but not in payload
					],
				} as EventParams; // casting as currency and value aren't required in payload but are required in type

				// As of 06/27/2024, there is only the requirement to fire the GA event
				// from the cart -> quick entry flow
				sendGAEvent<EventParams>(
					GoogleAnalyticsEventName.ADD_TO_CART,
					addToCartGAEventParams
				);
			} else if (dataType === QuickEntryDataType.ShoppingList) {
				if (isLoggedIn) {
					const requestBody = {
						zipCode: getZipCode(
							isLoggedIn,
							zipCode,
							selectedShipToAddress,
							customerPickup,
							salesPlant
						),

						items: [
							{
								shoppingListId: id,
								ryersonSku: selectedProduct.materialMaster,
								quantity: quantity,
								customerPartNumber: getCustomerPartNumber(
									selectedProduct.altPartNumber
								),
								quantityUom: qtyUOM,
								pricingUom: priceUOM,
								domesticRequired: false,
							} as ShoppingListProduct,
						],
					};
					addProductsToShoppingList(
						requestBody,
						id,
						isLoggedIn,
						axiosInstance,
						(response: any) => {
							if (response?.data) {
								const responsedCartData: ShoppingListItem[] = response.data.map(
									(item: any) => {
										return {
											...item,
											materialWidth: selectedProduct.materialWidth,
											materialLengthMin: selectedProduct.materialLengthMin,
											itemDescriptionLocalization:
												selectedProduct.itemDescriptionLocalization,
											fastPath: selectedProduct.fastPath,
										} as ShoppingListItem;
									}
								);
								let allShoppingListItems = [...data, ...responsedCartData];
								callBack && callBack();
								handleOnSuccessAddItem(allShoppingListItems);
							}
						}
					);
				} else {
					const currentShoppingListData = JSON.parse(
						getItem("addToShoppingList") || JSON.stringify([])
					);
					let finalData: any = [];
					if (currentShoppingListData.length > 0) {
						finalData = [...currentShoppingListData, addToObject];
					} else {
						finalData = new Array(addToObject);
					}

					setShoppingListData(finalData, 5);
					// Now check whether cookies are added or not
					const getShoppingListItems = JSON.parse(getItem("addToShoppingList"));

					if (getShoppingListItems.length === finalData.length) {
						sessionStorage.setItem("shoppingListLastIndex", (lastIndex + 1).toString());
						let allShoppingListItems = [...data, newlyAddedDataObject];
						callBack && callBack();
						handleOnSuccessAddItem(allShoppingListItems);
					}
				}
			} else if (dataType === QuickEntryDataType.QuoteList) {
				const itemNumberValue = 1;
				let requestBody = {
					quoteId: id,
					skuNumber: selectedProduct.materialMaster,
					itemNumber: itemNumber ? itemNumber + itemNumberValue : itemNumberValue,
					customerReferenceNumber: "",
					customerPartNumber: getCustomerPartNumber(selectedProduct?.altPartNumber),
					purchaseOrderNumber: "",
					quantity: quantity,
					quantityUom: qtyUOM,
					pricingUom: priceUOM,
					requestedDeliveryDate: "",
					domesticRequired: false,
					notes: "",
					product: null,
					form: selectedProduct.form,
					shape: selectedProduct.shape,
					finish: null,
					grade: "",
					width: 0,
					length: 0,
					thickness: 0,
					category: selectedProduct.category,
					cutFlag: selectedProduct.cutFlag,
					orderable: selectedProduct.orderable,
					fastPath: selectedProduct.fastPath,
					mageniumProductId: "",
					weightPerFoot: selectedProduct.weightPerFoot,
					weightPerPiece: selectedProduct.weightPerPiece,
					weightPerSquareFoot: selectedProduct.weightPerSquareFoot,
				};

				addLineItemAPI(
					axiosInstance,
					id,
					requestBody,
					(response) => {
						if (isAxiosError(response) || (response as AxiosResponse).status !== 200) {
							throw response;
						}
						const newItem = (response as AxiosResponse).data;
						let allItems = [...data, newItem];
						callBack && callBack();
						handleOnSuccessAddItem(allItems);
					},
					(err) => {
						callBack && callBack();
						handleOnFailureAddItem();
					}
				);
			}
		}
	};

	const handleOnFailureAddItem = () => {
		setSearchTerm("");
		setDisplaySearchTerm("");
		setItemAddSuccess(false);
		setItemAddFail(true);
	};

	const handleOnSuccessAddItem = (updateProducts: QuickEntryData[]) => {
		if (dataType === QuickEntryDataType.ShoppingCart) {
			//TODO: Remove this condition when shopping list has pricing data

			setPricingOfItems && setPricingOfItems(defaultPricingData);
			setCartPricingData(defaultPricingData);
		}
		setSearchTerm("");
		setData(updateProducts);
		setDisplaySearchTerm("");
		setItemAddSuccess(true);
		setItemAddFail(false);
	};

	const closeQuickEntry = () => {
		setShowQuickEntry(false);
		setQuantity(undefined);
		setSearchTerm("");
	};

	const quickEntryProps: QuickEntryProps = {
		searchInputContainerRef,
		showDropdown,
		setShowDropdown,
		searchResult,
		onClickSelectItem,
		isError,
		quantity,
		setQuantity,
		selectedProduct,
		qtyUOM,
		qtyUOMDropdown,
		priceUOM,
		priceUOMDropdown,
		onClickAddBelow,
		displaySearchTerm,
		onChangeText,
		searchTerm,
		setPriceUOM,
		showQuickEntry,
		closeQuickEntry,
		itemAddSuccess,
		itemAddFail,
		updateQuantityUom,
		isQuickEntrySubmitted,
		setIsQuickEntrySubmitted,
		handleValidationResponse,
		hasMounted,
		stateMessenger,
	};

	return (
		<>
			<div id="quick-entry">
				<Media lessThan="lg">
					<QuickEntryMobile {...quickEntryProps} />
				</Media>
				<Media greaterThanOrEqual="lg">
					<QuickEntryDesktop {...quickEntryProps} />
				</Media>
			</div>
		</>
	);
};

export default QuickEntry;
